import type { GuideSummary, Step, WorkspaceSummary } from '@nolens/domain';
import { formatRelative } from '@nolens/i18n';
import { Badge, cx, Shot, SiteIcon, useLocale } from '@nolens/ui';
import { Link } from 'react-router';
import { useGuideImage } from '../lib/images';
import { spaceName } from '../lib/locale';
import { usePref } from '../lib/prefs';
import { workspaceText } from '../i18n/workspace';
import { GridIcon, ListIcon } from './Icons';
import { SpaceTile } from './SpaceTile';

const VIEWS = ['grid', 'list'] as const;
export type LibraryView = (typeof VIEWS)[number];

/** Grelha ou lista: a escolha fica neste browser, igual em todas as listas de guias. */
export const useLibraryView = () => usePref<LibraryView>('nolens:view', VIEWS, 'grid');

export function ViewToggle({
  value,
  onChange,
}: {
  value: LibraryView;
  onChange: (v: LibraryView) => void;
}) {
  const t = workspaceText(useLocale());
  const item = (v: LibraryView, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      aria-pressed={value === v}
      aria-label={label}
      title={label}
      onClick={() => onChange(v)}
      className={cx(
        'inline-flex size-8 items-center justify-center rounded-full transition',
        value === v
          ? 'bg-surface-1 text-text-1 shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
          : 'text-text-3 hover:text-text-1',
      )}
    >
      {icon}
    </button>
  );
  return (
    <div
      role="group"
      aria-label={t.view}
      className="inline-flex gap-0.5 rounded-full bg-surface-2 p-1"
    >
      {item('grid', t.grid, <GridIcon size={16} />)}
      {item('list', t.list, <ListIcon size={16} />)}
    </div>
  );
}

/** Quem gravou, curto: a parte do email antes do @. */
const who = (email: string | null) => email?.split('@')[0] ?? null;

function coverStep(g: GuideSummary): Step | null {
  return g.cover
    ? ({
        ...g.cover,
        id: 'cover',
        title: '',
        description: '',
        url: '',
        pageTitle: '',
        createdAt: 0,
      } as Step)
    : null;
}

function GuideCard({ guide, space }: { guide: GuideSummary; space?: WorkspaceSummary }) {
  const locale = useLocale();
  const t = workspaceText(locale);
  const cover = coverStep(guide);
  const src = useGuideImage(guide.id, cover?.imageId);
  const author = who(guide.authorEmail);
  return (
    <li>
      <Link
        to={`/g/${guide.id}`}
        className="group flex h-full flex-col rounded-[16px] bg-surface-1 p-2 shadow-[inset_0_0_0_1px_var(--border)] transition hover:shadow-[inset_0_0_0_1px_var(--border-strong),0_8px_24px_-16px_rgb(0_0_0/0.3)]"
      >
        {cover ? (
          <Shot step={cover} src={src} className="aspect-video" />
        ) : (
          <div className="flex aspect-video items-center justify-center rounded-[10px] bg-surface-2">
            {guide.site && <SiteIcon site={guide.site} className="size-8 opacity-70" />}
          </div>
        )}
        <div className="flex flex-1 flex-col px-1.5 pb-1 pt-3">
          {guide.site && (
            <p className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-text-3">
              <SiteIcon site={guide.site} />
              <span className="truncate">{guide.site.name}</span>
            </p>
          )}
          <p className="mt-1 line-clamp-2 font-medium leading-snug">{guide.title || t.untitled}</p>
          <div className="flex-1" />
          {space && (
            <p className="mt-2.5">
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-surface-2 py-0.5 pl-1 pr-2 text-[12px] text-text-2">
                <SpaceTile ws={space} size={14} />
                <span className="truncate">{spaceName(space, locale)}</span>
              </span>
            </p>
          )}
          <p
            className={cx(
              'flex min-w-0 items-center gap-1.5 text-[12.5px] text-text-3',
              space ? 'mt-1.5' : 'mt-2.5',
            )}
          >
            <span className="shrink-0" title={new Date(guide.updatedAt).toLocaleString(locale)}>
              {formatRelative(locale, guide.updatedAt)}
            </span>
            {author && (
              <>
                <span aria-hidden="true">·</span>
                <span className="min-w-0 truncate" title={guide.authorEmail ?? undefined}>
                  {author}
                </span>
              </>
            )}
            <span aria-hidden="true">·</span>
            <span className="shrink-0">{t.steps(guide.actions)}</span>
          </p>
          {guide.visibility === 'link' && (
            <p className="mt-2">
              <Badge tone="accent">{t.publicLink}</Badge>
            </p>
          )}
        </div>
      </Link>
    </li>
  );
}

function GuideRow({ guide, space }: { guide: GuideSummary; space?: WorkspaceSummary }) {
  const locale = useLocale();
  const t = workspaceText(locale);
  const author = who(guide.authorEmail);
  return (
    <li>
      <Link
        to={`/g/${guide.id}`}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-4 py-3 transition hover:bg-surface-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,160px)_80px_110px]"
      >
        <span className="flex min-w-0 items-center gap-3">
          {guide.site ? (
            <SiteIcon site={guide.site} className="size-5" />
          ) : (
            <span className="size-5 shrink-0 rounded-[4px] bg-surface-2" />
          )}
          <span className="truncate font-medium">{guide.title || t.untitled}</span>
          {guide.visibility === 'link' && (
            <span className="hidden shrink-0 sm:inline-flex">
              <Badge tone="accent">{t.publicLink}</Badge>
            </span>
          )}
        </span>
        <span className="hidden min-w-0 items-center gap-1.5 truncate text-[13.5px] text-text-2 sm:flex">
          {space ? (
            <>
              <SpaceTile ws={space} size={16} />
              <span className="truncate">{spaceName(space, locale)}</span>
            </>
          ) : (
            <span className="truncate" title={guide.authorEmail ?? undefined}>
              {author ?? '—'}
            </span>
          )}
        </span>
        <span className="hidden text-[13.5px] text-text-3 sm:block">{guide.actions}</span>
        <span
          className="text-right text-[13px] text-text-3"
          title={new Date(guide.updatedAt).toLocaleString(locale)}
        >
          {formatRelative(locale, guide.updatedAt)}
        </span>
      </Link>
    </li>
  );
}

/**
 * Guias em grelha (com a captura do 1.º passo, zoom no sítio do clique) ou em lista.
 * `spaces`: mostra em que espaço está cada guia (listas de vários espaços).
 */
export function GuideCollection({
  guides,
  view,
  spaces,
}: {
  guides: GuideSummary[];
  view: LibraryView;
  spaces?: Map<string, WorkspaceSummary>;
}) {
  const t = workspaceText(useLocale());
  if (view === 'list')
    return (
      <div className="overflow-hidden rounded-[16px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]">
        <div
          aria-hidden="true"
          className="hidden grid-cols-[minmax(0,1fr)_minmax(0,160px)_80px_110px] gap-4 border-b border-border px-4 py-2 text-[12px] font-medium uppercase tracking-wide text-text-3 sm:grid"
        >
          <span className="pl-8">{t.colGuide}</span>
          <span>{spaces ? t.colSpace : t.colAuthor}</span>
          <span>{t.colSteps}</span>
          <span className="text-right">{t.colUpdated}</span>
        </div>
        <ul className="divide-y divide-border" data-view="list">
          {guides.map((g) => (
            <GuideRow key={g.id} guide={g} space={spaces?.get(g.workspaceId)} />
          ))}
        </ul>
      </div>
    );
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-view="grid">
      {guides.map((g) => (
        <GuideCard key={g.id} guide={g} space={spaces?.get(g.workspaceId)} />
      ))}
    </ul>
  );
}
